import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, Trash2, Factory, Pencil, ImagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/StatusBadge";
import { AuthImage } from "@/components/AuthImage";
import { DateField } from "@/components/DateField";
import api, { fmt, fmtDate, errText, CATEGORIES, undoToast } from "@/lib/api";

const emptyForm = () => ({ design_name: "", category: "Suit", production_date: format(new Date(), "yyyy-MM-dd"), total_pieces: 0, fabric_cost: 0, stitching_cost: 0, karigar_cost: 0, other_cost: 0, expected_price: 0, dispatches: [] });

export default function Production() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [filter, setFilter] = useState("All");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const imgRef = useRef(null);

  const { data: designs, isLoading } = useQuery({ queryKey: ["productions"], queryFn: async () => (await api.get("/productions")).data });
  const { data: karigars } = useQuery({ queryKey: ["karigars"], queryFn: async () => (await api.get("/karigars")).data });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        total_pieces: Number(form.total_pieces) || 0,
        fabric_cost: Number(form.fabric_cost) || 0,
        stitching_cost: Number(form.stitching_cost) || 0,
        karigar_cost: Number(form.karigar_cost) || 0,
        other_cost: Number(form.other_cost) || 0,
        expected_price: Number(form.expected_price) || 0,
        dispatches: form.dispatches.filter((d) => d.karigar_id).map((d) => ({ ...d, sent: Number(d.sent) || 0, received: Number(d.received) || 0 })),
      };
      const doc = editId ? (await api.put(`/productions/${editId}`, body)).data : (await api.post("/productions", body)).data;
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await api.post(`/productions/${doc.id}/image`, fd);
      }
      return doc;
    },
    onSuccess: () => {
      toast.success(editId ? "Design updated" : "Design added");
      qc.invalidateQueries({ queryKey: ["productions"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      close();
    },
    onError: (e) => toast.error(errText(e)),
  });

  const remove = useMutation({
    mutationFn: async (pid) => (await api.delete(`/productions/${pid}`)).data,
    onSuccess: (data) => { undoToast("Design deleted", data.undo_id, () => qc.invalidateQueries({ queryKey: ["productions"] })); qc.invalidateQueries({ queryKey: ["productions"] }); },
  });

  const close = () => { setOpen(false); setEditId(null); setForm(emptyForm()); setImageFile(null); };
  const openEdit = (d) => {
    setEditId(d.id);
    setImageFile(null);
    setForm({ design_name: d.design_name, category: d.category, production_date: d.production_date || "", total_pieces: d.total_pieces, fabric_cost: d.fabric_cost, stitching_cost: d.stitching_cost, karigar_cost: d.karigar_cost, other_cost: d.other_cost, expected_price: d.expected_price, dispatches: d.dispatches.map((x) => ({ karigar_id: x.karigar_id, sent: x.sent, received: x.received, sent_date: x.sent_date, received_date: x.received_date || "" })) });
    setOpen(true);
  };
  const setDispatch = (i, k, v) => setForm({ ...form, dispatches: form.dispatches.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)) });

  const filters = ["All", ...(karigars?.map((k) => k.name) || []), "Ready", "Pending", "Completed"];
  const filtered = (designs || []).filter((d) => {
    if (filter === "All") return true;
    if (filter === "Completed") return d.status === "Completed";
    if (filter === "Ready") return d.ready_pieces > 0;
    if (filter === "Pending") return d.pending_pieces > 0;
    return d.dispatches.some((x) => x.karigar_name === filter);
  });

  return (
    <div className="space-y-6" data-testid="production-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight">Shop Production</h1>
          <p className="text-sm text-muted-foreground mt-1">Independent production designs & karigar dispatch</p>
        </div>
        <Button data-testid="add-design-button" className="rounded-full active:scale-95 transition-transform" onClick={() => { setForm(emptyForm()); setEditId(null); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-1.5" /> New Design
        </Button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {filters.map((f) => (
          <button
            key={f}
            data-testid={`production-filter-${f.toLowerCase()}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${filter === f ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground hover:text-foreground"}`}
          >
            {f}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid md:grid-cols-2 gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-48 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border bg-card p-12 text-center">
          <Factory className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="mt-3 font-medium">No designs found</p>
          <p className="text-sm text-muted-foreground">Add a design to start shop production.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((d) => (
            <div key={d.id} data-testid={`design-card-${d.id}`} className="rounded-xl border bg-card p-6 space-y-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  {d.image && (
                    <AuthImage src={`/productions/${d.id}/image`} alt={d.design_name} className="h-14 w-14 rounded-lg object-cover shrink-0 border" />
                  )}
                  <div className="min-w-0">
                    <p className="font-heading font-semibold truncate">{d.design_name}</p>
                    <p className="text-xs text-muted-foreground">{d.category}{d.production_date && ` · ${fmtDate(d.production_date)}`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <StatusBadge status={d.status} />
                  <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`edit-design-${d.id}`} onClick={() => openEdit(d)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" data-testid={`delete-design-${d.id}`} onClick={() => setDeleteTarget(d)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                  <span>{d.ready_pieces} ready of {d.total_pieces} pieces</span>
                  <span>{d.progress}%</span>
                </div>
                <Progress value={d.progress} className="h-2" />
              </div>

              <div className="grid grid-cols-3 gap-3 text-sm">
                {[["Total", d.total_pieces], ["Ready", d.ready_pieces], ["Pending", d.pending_pieces]].map(([l, v]) => (
                  <div key={l}><p className="text-xs text-muted-foreground">{l}</p><p className="font-medium">{v}</p></div>
                ))}
              </div>

              {d.dispatches.length > 0 && (
                <div className="border-t pt-3 space-y-1.5">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Karigar Dispatch</p>
                  {d.dispatches.map((x, i) => (
                    <div key={`${x.karigar_id || x.karigar_name}-${i}`} className="flex justify-between text-sm">
                      <span>{x.karigar_name}</span>
                      <span className="text-muted-foreground">Sent {x.sent} · Received {x.received} · Pending {Math.max(x.sent - x.received, 0)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t pt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><p className="text-xs text-muted-foreground">Total Cost</p><p className="font-medium">{fmt(d.total_cost)}</p></div>
                <div><p className="text-xs text-muted-foreground">Cost/Piece</p><p className="font-medium">{fmt(d.cost_per_piece)}</p></div>
                <div><p className="text-xs text-muted-foreground">Exp. Price</p><p className="font-medium">{fmt(d.expected_price)}</p></div>
                <div><p className="text-xs text-muted-foreground">Exp. Profit</p><p className={`font-medium ${d.expected_profit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{fmt(d.expected_profit)}</p></div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Design" : "New Production Design"}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Design Name *</Label>
                <Input data-testid="design-name-input" value={form.design_name} onChange={(e) => setForm({ ...form, design_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Design Image</Label>
                <input ref={imgRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" className="hidden" data-testid="design-image-input"
                  onChange={(e) => { setImageFile(e.target.files?.[0] || null); e.target.value = ""; }} />
                <div className="flex items-center gap-3">
                  {imageFile ? (
                    <img src={URL.createObjectURL(imageFile)} alt="preview" className="h-10 w-10 rounded-lg object-cover border" />
                  ) : editId && designs?.find((x) => x.id === editId)?.image ? (
                    <AuthImage src={`/productions/${editId}/image`} alt="current" className="h-10 w-10 rounded-lg object-cover border" />
                  ) : null}
                  <Button type="button" variant="outline" size="sm" data-testid="design-image-button" className="rounded-lg" onClick={() => imgRef.current?.click()}>
                    <ImagePlus className="h-3.5 w-3.5 mr-1.5" /> {imageFile ? imageFile.name.slice(0, 18) : "Upload Image"}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="design-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Date</Label>
                <DateField testId="design-date-field" value={form.production_date} onChange={(v) => setForm({ ...form, production_date: v })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Total Pieces</Label>
                <Input data-testid="design-total-pieces-input" type="number" min="0" value={form.total_pieces} onChange={(e) => setForm({ ...form, total_pieces: e.target.value })} />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Karigar — Assigned, Sent & Received</Label>
              {form.dispatches.map((d, i) => (
                <div key={i} className="rounded-lg border p-3 space-y-2">
                  <div className="grid grid-cols-[1fr_70px_70px_36px] gap-2 items-center">
                    <Select value={d.karigar_id} onValueChange={(v) => setDispatch(i, "karigar_id", v)}>
                      <SelectTrigger data-testid={`dispatch-karigar-${i}`}><SelectValue placeholder="Assigned Karigar" /></SelectTrigger>
                      <SelectContent>{karigars?.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input data-testid={`dispatch-sent-${i}`} type="number" min="0" placeholder="Sent" value={d.sent} onChange={(e) => setDispatch(i, "sent", e.target.value)} />
                    <Input data-testid={`dispatch-received-${i}`} type="number" min="0" placeholder="Recd" value={d.received} onChange={(e) => setDispatch(i, "received", e.target.value)} />
                    <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setForm({ ...form, dispatches: form.dispatches.filter((_, idx) => idx !== i) })}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Sent to Karigar Date</span>
                      <DateField testId={`dispatch-sent-date-${i}`} value={d.sent_date} onChange={(v) => setDispatch(i, "sent_date", v)} />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Receive From Karigar Date</span>
                      <DateField testId={`dispatch-received-date-${i}`} value={d.received_date || ""} onChange={(v) => setDispatch(i, "received_date", v)} />
                    </div>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="add-dispatch-button" className="rounded-lg" onClick={() => setForm({ ...form, dispatches: [...form.dispatches, { karigar_id: "", sent: 0, received: 0, sent_date: format(new Date(), "yyyy-MM-dd"), received_date: "" }] })}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Karigar
              </Button>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Costing</Label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[["karigar_cost", "Karigar ₹"], ["other_cost", "Material ₹"], ["fabric_cost", "Fabric ₹"], ["expected_price", "Customer Price ₹"]].map(([k, l]) => (
                  <div key={k} className="space-y-1">
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</span>
                    <Input data-testid={`design-${k.replace(/_/g, "-")}-input`} type="number" min="0" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                  </div>
                ))}
                <div className="space-y-1">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Cost</span>
                  <div data-testid="design-total-cost" className="h-9 flex items-center px-3 rounded-md border bg-muted/40 text-sm font-medium">
                    {fmt((Number(form.karigar_cost) || 0) + (Number(form.other_cost) || 0) + (Number(form.fabric_cost) || 0) + (Number(form.stitching_cost) || 0))}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Karigar cost automatically karigar ke account me payable ban jata hai.
                {Number(form.stitching_cost) > 0 && <span className="ml-1 text-amber-500">Total me purana stitching cost ₹{Number(form.stitching_cost).toLocaleString("en-IN")} bhi shamil hai.</span>}
              </p>
            </div>

            <Button data-testid="design-save-button" className="w-full rounded-lg" disabled={!form.design_name.trim() || save.isPending} onClick={() => save.mutate()}>
              {editId ? "Update Design" : "Save Design"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-design-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete "{deleteTarget?.design_name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Ye design aur iski saari dispatch history permanently delete ho jayegi. Ye action undo nahi ho sakta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-design-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-design-confirm" className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => { remove.mutate(deleteTarget.id); setDeleteTarget(null); }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
